import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  CardContent,
  Collapse,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CircularProgress from '@mui/material/CircularProgress';

const PendingOrderCard = ({ order, isMobile, onCancel, cancelling }) => {
  const theme = useTheme();
  
  const getOrderTypeColor = (orderType) => {
    switch (orderType) {
      case 'MARKET': return theme.palette.info.main;
      case 'LIMIT': return theme.palette.primary.main;
      case 'SL': return theme.palette.warning.main;
      case 'SLM': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getStatusColor = (status) => {
    if (status.includes('PENDING')) return theme.palette.warning.main;
    if (status.includes('CONFIRMED')) return theme.palette.success.main;
    if (status.includes('REJECTED')) return theme.palette.error.main;
    return theme.palette.info.main;
  };

  return (
    <Grow in timeout={300}>
      <Card 
        elevation={0}
        sx={{ 
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 2,
          mb: 1,
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: theme.shadows[2],
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
          '&:last-child': { mb: 0 }
        }}
      >
        <CardContent sx={{ 
          py: isMobile ? 1.25 : 1.5, 
          px: isMobile ? 1.5 : 2, 
          '&:last-child': { pb: isMobile ? 1.25 : 1.5 } 
        }}>
          {isMobile ? (
            // Mobile Layout
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Chip
                    label={order.order_side}
                    size="small"
                    variant="filled"
                    sx={{ 
                      minWidth: 42,
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 'bold',
                      backgroundColor: order.order_side === 'BUY' ? theme.palette.success.main : theme.palette.error.main,
                      color: 'white',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                  <Chip
                    label={order.order_type}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      height: 22,
                      fontSize: '0.6rem',
                      fontWeight: 'medium',
                      borderColor: getOrderTypeColor(order.order_type),
                      color: getOrderTypeColor(order.order_type),
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                  <Chip
                    label={order.product}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      height: 22,
                      fontSize: '0.6rem',
                      color: 'text.secondary',
                      borderColor: alpha(theme.palette.text.secondary, 0.3),
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                </Box>
                
                <Box>
                  <Tooltip title="Cancel Order" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => onCancel(order, e)}
                      disabled={
                        order.order_status === "EXCHANGE_RESPONSE_PENDING" ||
                        cancelling === order.oms_order_id
                      }
                      sx={{
                        p: 0.5,
                        borderRadius: 1.5,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        color: 'error.main',
                        '&:hover': { 
                          backgroundColor: alpha(theme.palette.error.main, 0.2),
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {cancelling === order.oms_order_id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CancelIcon sx={{ fontSize: '1rem' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
                gap: 1.5, 
                alignItems: 'center',
                py: 0.5
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                  Qty: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>{order.quantity}</Box>
                </Typography>
                {order.price > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                    Price: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }}>₹{order.price.toFixed(2)}</Box>
                  </Typography>
                )}
                <Chip
                  label={order.order_status}
                  size="small"
                  variant="filled"
                  sx={{ 
                    height: 20,
                    fontSize: '0.6rem',
                    backgroundColor: alpha(getStatusColor(order.order_status), 0.15),
                    color: getStatusColor(order.order_status),
                    fontWeight: 'medium',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              </Box>
            </Box>
          ) : (
            // Desktop Layout
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                <Chip
                  label={order.order_side}
                  size="small"
                  variant="filled"
                  sx={{ 
                    minWidth: 50,
                    height: 24,
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    backgroundColor: order.order_side === 'BUY' ? theme.palette.success.main : theme.palette.error.main,
                    color: 'white'
                  }}
                />
                <Chip
                  label={order.order_type}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    height: 24,
                    fontSize: '0.65rem',
                    fontWeight: 'medium',
                    borderColor: getOrderTypeColor(order.order_type),
                    color: getOrderTypeColor(order.order_type)
                  }}
                />
                <Chip
                  label={order.product}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    height: 24,
                    fontSize: '0.65rem',
                    color: 'text.secondary',
                    borderColor: alpha(theme.palette.text.secondary, 0.3)
                  }}
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  Qty: <strong>{order.quantity}</strong>
                </Typography>
                {order.price > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Price: <strong>₹{order.price.toFixed(2)}</strong>
                  </Typography>
                )}
                
                <Chip
                  label={order.order_status}
                  size="small"
                  variant="filled"
                  sx={{ 
                    height: 22,
                    fontSize: '0.65rem',
                    backgroundColor: alpha(getStatusColor(order.order_status), 0.15),
                    color: getStatusColor(order.order_status),
                    fontWeight: 'medium'
                  }}
                />
              </Box>
              
              <Box>
                <Tooltip title="Cancel Order" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => onCancel(order, e)}
                    disabled={
                      order.order_status === "EXCHANGE_RESPONSE_PENDING" ||
                      cancelling === order.oms_order_id
                    }
                    sx={{
                      p: 0.75,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      color: 'error.main',
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.error.main, 0.2),
                        transform: 'scale(1.05)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {cancelling === order.oms_order_id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <CancelIcon sx={{ fontSize: '1.1rem' }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );
};

const PendingOrdersSection = ({ 
  orders, 
  expanded, 
  onToggle, 
  onCancel, 
  cancelling,
  isSquareOffOrder,
  stopLossOrdersCount 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!orders || orders.length === 0) return null;

  return (
    <Fade in timeout={300}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.02)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
        }}
      >
        <Box
          onClick={onToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: isMobile ? 1.25 : 1.75,
            cursor: 'pointer',
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.warning.main, 0.12),
              transform: 'translateY(-1px)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PendingActionsIcon 
              sx={{ 
                color: theme.palette.warning.main,
                fontSize: isMobile ? '1.2rem' : '1.4rem'
              }} 
            />
            <Typography 
              variant={isMobile ? "body2" : "subtitle2"} 
              fontWeight="600" 
              color="warning.main"
            >
              {isSquareOffOrder ? 'Pending Orders' : 'Pending Orders'}
            </Typography>
            <Chip 
              label={orders.length} 
              size="small" 
              variant="filled"
              sx={{
                backgroundColor: theme.palette.warning.main,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                height: 22,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Box>
          <IconButton 
            size="small"
            sx={{
              color: theme.palette.warning.main,
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.warning.main, 0.2),
                transform: 'rotate(180deg)'
              }
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Collapse in={expanded} timeout={300}>
          <Box sx={{ 
            p: isMobile ? 1.25 : 1.75,
            pt: 0,
          }}>
            {/* Square-off warning message */}
            {isSquareOffOrder && stopLossOrdersCount > 0 && (
              <Alert 
                severity="warning" 
                variant="filled"
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.warning.main, 0.9),
                  color: 'white',
                  fontWeight: 'medium',
                  '& .MuiAlert-icon': {
                    color: 'white'
                  }
                }}
              >
                <Typography variant="body2" fontWeight="medium">
                  Please cancel all existing pending orders below to place this square-off order
                </Typography>
              </Alert>
            )}
            
            <Box sx={{
              maxHeight: isMobile ? 280 : 240, 
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: alpha(theme.palette.divider, 0.1),
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.warning.main, 0.3),
                borderRadius: 3,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.warning.main, 0.5),
                }
              }
            }}>
              {orders.map((order) => (
                <PendingOrderCard
                  key={order.oms_order_id}
                  order={order}
                  isMobile={isMobile}
                  onCancel={onCancel}
                  cancelling={cancelling}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  );
};

export default PendingOrdersSection; 